/* Browser drafts and customer-scoped Shopify metafields. No Admin credentials. */
(() => {
  'use strict';
  const model = window.KillinBinderModel;
  const CONFIG = document.querySelector('[data-binder-builder-config]');
  if (!CONFIG || !model) return;
  const KIOSK = document.body.hasAttribute('data-kiosk');
  const LOCAL = 'killin-binders-v1:';
  const TOKEN = 'killin-binder-account-session-v1';
  const TRANSACTION = 'killin-binder-account-login-v1';
  let state = model.empty(), base = model.empty(), owner = 'guest', status = 'Saved on this device';
  let session, discovery, timer, running = false, dirty = false, conflict = null, persisted = false;
  function storageRead(storage, key) {
    try { return JSON.parse(storage.getItem(key) || 'null'); } catch (_) { return null; }
  }
  function storageWrite(storage, key, value) {
    try { storage.setItem(key, JSON.stringify(value)); return true; } catch (_) { return false; }
  }
  function notify() { document.dispatchEvent(new CustomEvent('killin:builder-state')); }
  function localSave() {
    if (KIOSK) { persisted = false; status = 'Kiosk draft'; return; }
    persisted = storageWrite(localStorage, LOCAL + owner, state);
    if (!persisted) status = 'Your latest changes could not be saved on this device. Keep this page open.';
  }
  async function request(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 25000);
    try {
      const response = await fetch(url, {...options, signal: controller.signal});
      if (!response.ok) throw Error(response.status === 401 ? 'Sign in again to sync your binders.' : 'Account sync is temporarily unavailable. Your draft is saved on this device.');
      return await response.json();
    } finally { clearTimeout(timeout); }
  }
  function endpoint(value) {
    const url = new URL(value);
    if (url.protocol !== 'https:' || !(url.hostname === location.hostname || url.hostname === 'shopify.com' || url.hostname.endsWith('.shopify.com'))) throw Error('Customer account endpoint unavailable.');
    return url.href;
  }
  async function discover() {
    if (!discovery) {
      const [auth, api] = await Promise.all([
        request('/.well-known/openid-configuration'), request('/.well-known/customer-account-api')
      ]);
      discovery = {authorize: endpoint(auth.authorization_endpoint), token: endpoint(auth.token_endpoint),
        logout: endpoint(auth.end_session_endpoint), graphql: endpoint(api.graphql_api)};
    }
    return discovery;
  }
  function base64url(bytes) {
    return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  async function login() {
    if (KIOSK) throw Error('Transfer this binder to your phone to sign in.');
    if (!CONFIG.dataset.clientId) throw Error('Account saving is being connected. Your binder is saved on this device.');
    const endpoints = await discover();
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    const verifier = base64url(bytes), nonce = model.id();
    const challenge = base64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
    const transaction = {state: nonce, verifier, created: Date.now(), redirect: location.origin + '/pages/binder',
      preview: new URL(location.href).searchParams.get('builder_preview') === '1'};
    if (!storageWrite(sessionStorage, TRANSACTION, transaction)) throw Error('Allow site storage to sign in and save your binder.');
    const url = new URL(endpoints.authorize);
    url.search = new URLSearchParams({client_id: CONFIG.dataset.clientId, redirect_uri: transaction.redirect,
      response_type: 'code', scope: 'openid email customer-account-api:full', state: nonce,
      code_challenge: challenge, code_challenge_method: 'S256'}).toString();
    location.assign(url.href);
  }
  async function token() {
    if (!session) throw Error('Sign in to save across devices.');
    if (session.expires > Date.now() + 30000) return session.access;
    if (!session.refresh) throw Error('Sign in again to sync your binders.');
    const endpoints = await discover();
    const result = await request(endpoints.token, {method: 'POST', headers: {'Content-Type': 'application/x-www-form-urlencoded'},
      body: new URLSearchParams({grant_type: 'refresh_token', client_id: CONFIG.dataset.clientId, refresh_token: session.refresh}).toString()});
    session = {access: result.access_token, refresh: result.refresh_token || session.refresh, expires: Date.now() + Number(result.expires_in) * 1000};
    storageWrite(sessionStorage, TOKEN, session);
    return session.access;
  }
  async function graphql(query, variables = {}) {
    const endpoints = await discover();
    const access = await token();
    const result = await request(endpoints.graphql, {method: 'POST', headers: {'Content-Type': 'application/json', Authorization: access}, body: JSON.stringify({query, variables})});
    if (result.errors?.length) throw Error('Your account could not save this update. Your local draft is safe.');
    return result.data;
  }
  async function readAccount() {
    const keys = Object.keys(model.documents(model.empty()));
    const fields = keys.map((key, index) => `f${index}:metafield(namespace:$namespace,key:${JSON.stringify(key)}){value compareDigest}`).join('\n');
    const data = await graphql(`query($namespace:String!){customer{id ${fields}}}`, {namespace: CONFIG.dataset.namespace});
    if (!data.customer?.id) throw Error('Sign in to save across devices.');
    const documents = {}, hashes = {};
    keys.forEach((key, index) => {
      const field = data.customer['f' + index];
      try { documents[key] = field ? JSON.parse(field.value) : model.documents(model.empty())[key]; }
      catch (_) { throw Error('Saved account data needs recovery. Your local draft is safe.'); }
      hashes[key] = field?.compareDigest || null;
    });
    return {id: data.customer.id, documents, digests: hashes, state: model.fromDocuments(documents)};
  }
  async function saveAccount(snapshot, previous) {
    const remote = await readAccount();
    if (remote.id !== owner) throw Error('Your signed-in account changed. Reload before saving.');
    const merged = model.merge(previous, snapshot, remote.state);
    if (merged.conflicts.length) {
      conflict = {...merged, theirs: remote.state};
      throw Error('This slot changed on another device. Choose which version to keep.');
    }
    const next = model.documents(model.normalize(merged.value));
    const changed = Object.keys(next).filter(key => !model.equal(next[key], remote.documents[key]));
    let hashes = remote.digests;
    for (let start = 0; start < changed.length; start += 25) {
      const batch = changed.slice(start, start + 25);
      const metafields = batch.map(key => ({ownerId: owner, namespace: CONFIG.dataset.namespace,
        key, type: 'json', value: JSON.stringify(next[key]), compareDigest: hashes[key]}));
      const data = await graphql(`mutation($metafields:[MetafieldsSetInput!]!){metafieldsSet(metafields:$metafields){metafields{key compareDigest} userErrors{code message}}}`, {metafields});
      if (data.metafieldsSet.userErrors.length) throw Error('Your account changed while saving. Retry to merge the latest changes.');
      for (const field of data.metafieldsSet.metafields) hashes[field.key] = field.compareDigest;
    }
    return model.normalize(merged.value);
  }
  async function sync() {
    clearTimeout(timer);
    if (running || KIOSK || owner === 'guest' || conflict || !dirty) return;
    running = true;
    const snapshot = model.clone(state), previous = model.clone(base);
    status = 'Saving to your account…'; notify();
    try {
      const saved = await saveAccount(snapshot, previous);
      const newer = model.merge(snapshot, state, saved);
      base = saved;
      state = model.normalize(newer.value);
      dirty = !model.equal(state, base);
      status = dirty ? 'Saving your latest edits…' : 'Saved to your account';
      localSave();
    } catch (error) {
      status = error.message;
      dirty = true;
    } finally {
      running = false; notify();
      if (dirty && !conflict && status === 'Saving your latest edits…') timer = setTimeout(sync, 1200);
    }
  }
  function change(action) {
    const next = model.clone(state);
    action(next);
    const normalized = model.normalize(next);
    model.documents(normalized);
    state = normalized;
    dirty = true;
    status = owner === 'guest' ? 'Saved on this device' : 'Saved on this device · syncing…';
    localSave(); notify();
    clearTimeout(timer); timer = setTimeout(sync, 1200);
  }
  function resolveConflict(useMine) {
    if (!conflict) return;
    let value = model.clone(conflict.value);
    if (!useMine) {
      for (const path of conflict.conflicts) {
        const keys = path.split('.');
        let target = value, source = conflict.theirs;
        for (const key of keys.slice(0, -1)) { target = target[key]; source = source?.[key]; }
        const last = keys[keys.length - 1];
        if (source?.[last] === undefined) delete target[last]; else target[last] = model.clone(source[last]);
      }
    }
    base = conflict.theirs; conflict = null; state = model.normalize(value);
    dirty = true; localSave(); notify(); sync();
  }
  function guestDraft() { return model.normalize(storageRead(localStorage, LOCAL + 'guest')); }
  function importGuest() {
    const guest = guestDraft();
    change(next => {
      for (const binder of Object.values(guest.binders)) {
        if (Object.values(next.binders).some(value => value.uid === binder.uid)) continue;
        const slot = Array.from({length: model.MAX_BINDERS}, (_, i) => 'b' + i).find(key => !next.binders[key]);
        if (!slot) throw Error('Remove a saved binder before importing more drafts.');
        next.binders[slot] = binder;
      }
      Object.assign(next.owned, guest.owned);
      Object.assign(next.wishes, guest.wishes);
    });
  }
  async function logout() {
    const endpoints = await discover();
    clearTimeout(timer);
    sessionStorage.removeItem(TOKEN);
    if (owner !== 'guest') { localStorage.removeItem(LOCAL + owner); localStorage.removeItem(LOCAL + owner + ':base'); }
    session = null; owner = 'guest'; state = guestDraft(); base = model.empty(); dirty = false;
    status = 'Saved on this device'; notify();
    const url = new URL(endpoints.logout);
    url.searchParams.set('client_id', CONFIG.dataset.clientId);
    url.searchParams.set('post_logout_redirect_uri', location.origin + '/pages/binder?mode=builder');
    location.assign(url.href);
  }
  async function boot() {
    if (KIOSK) { status = 'Kiosk draft'; notify(); return; }
    state = guestDraft();
    const legacy = storageRead(localStorage, 'killin-wishlist-v1');
    for (const item of Array.isArray(legacy) ? legacy : []) if (item.key) state.wishes[item.key] = item;
    if (!CONFIG.dataset.clientId) { notify(); return; }
    try {
      const url = new URL(location.href);
      const callback = window.KillinBinderIncoming?.callback;
      if (callback || url.searchParams.has('code') || url.searchParams.has('error')) {
        const transaction = storageRead(sessionStorage, TRANSACTION);
        const code = callback ? callback.code : url.searchParams.get('code');
        const incomingState = callback ? callback.state : url.searchParams.get('state');
        const error = callback ? callback.error : url.searchParams.get('error');
        if (window.KillinBinderIncoming) delete window.KillinBinderIncoming.callback;
        for (const key of ['code', 'state', 'error', 'error_description']) url.searchParams.delete(key);
        url.searchParams.set('mode', 'builder'); history.replaceState(null, '', url);
        sessionStorage.removeItem(TRANSACTION);
        if (error) throw Error('Sign-in was cancelled. Your draft is saved on this device.');
        if (!transaction || incomingState !== transaction.state || Date.now() - transaction.created > 600000) throw Error('This sign-in link expired. Please sign in again.');
        const endpoints = await discover();
        const result = await request(endpoints.token, {method: 'POST', headers: {'Content-Type': 'application/x-www-form-urlencoded'},
          body: new URLSearchParams({grant_type: 'authorization_code', client_id: CONFIG.dataset.clientId,
            redirect_uri: transaction.redirect, code, code_verifier: transaction.verifier}).toString()});
        session = {access: result.access_token, refresh: result.refresh_token, expires: Date.now() + Number(result.expires_in) * 1000};
        storageWrite(sessionStorage, TOKEN, session);
      } else session = storageRead(sessionStorage, TOKEN);
      if (session) {
        const remote = await readAccount();
        owner = remote.id; base = remote.state;
        const cached = storageRead(localStorage, LOCAL + owner);
        // Pending local changes keep their original remote base for conflict detection.
        const pending = storageRead(localStorage, LOCAL + owner + ':base');
        if (cached && pending) {
          const merged = model.merge(model.normalize(pending), model.normalize(cached), remote.state);
          state = model.normalize(merged.value);
          if (merged.conflicts.length) conflict = {...merged, theirs: remote.state};
          dirty = !model.equal(state, base);
        } else state = remote.state;
        status = conflict ? 'Choose which device’s changes to keep.' : 'Saved to your account';
        localSave(); if (dirty && !conflict) sync();
      }
    } catch (error) { status = error.message; }
    notify();
  }
  document.addEventListener('killin:builder-state', () => {
    if (!KIOSK && owner !== 'guest') storageWrite(localStorage, LOCAL + owner + ':base', base);
  });
  window.addEventListener('storage', event => {
    if (event.key !== LOCAL + owner || running || dirty) return;
    state = model.normalize(storageRead(localStorage, event.key)); notify();
  });
  window.addEventListener('online', sync);
  window.KillinBinderStore = {change, sync, login, logout, importGuest, guestDraft, resolveConflict,
    get state() { return state; }, get status() { return status; }, get owner() { return owner; },
    get conflict() { return conflict; }, get configured() { return !!CONFIG.dataset.clientId; },
    get persisted() { return persisted; },
    resetKiosk() { if (KIOSK) { state = model.empty(); base = model.empty(); dirty = false; notify(); } }};
  window.KillinBinderStore.ready = boot();
})();
